import { useAppKitAccount, useAppKitProvider } from "@reown/appkit/react";
import { ethers } from "ethers";
import React, { useEffect, useState } from "react";
import {
  RiCoinsLine,
  RiFileCopy2Line,
  RiRefreshLine,
  RiStackLine,
  RiWalletLine,
} from "react-icons/ri";
import { toast } from "react-toastify";
import erc20Abi from "../abi/erc20.json";
import migrationAbi from "../abi/migration.json";
import { stakingApi } from "../services/stakingApi";
import stakingAbi from "../abi/staking.json";
import { Footer, Header } from "../components/blocksafu-ui";
import appChain56 from "../configs/appChain56.json";

export default function Homepage() {
  const [activeTab, setActiveTab] = useState("staking");
  const [stakingAmount, setStakingAmount] = useState("");
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [tokenBalance, setTokenBalance] = useState("0");
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [stakedBalance, setStakedBalance] = useState("0");
  const [pendingRewards, setPendingRewards] = useState("0");
  const [aprRate, setAprRate] = useState("360");
  const [apyRate, setApyRate] = useState("0");
  const [isLoadingAPY, setIsLoadingAPY] = useState(false);
  const [isStaking, setIsStaking] = useState(false);
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [allowanceAmount, setAllowanceAmount] = useState("0");
  const [balanceTokenV1, setBalanceTokenV1] = useState("0");
  const [balanceStakingCompoundV1, setBalanceStakingCompoundV1] = useState("0");
  const [balanceStakingManualV1, setBalanceStakingManualV1] = useState("0");
  const [isMigrated, setIsMigrated] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [isLoadingMigration, setIsLoadingMigration] = useState(false);
  const [unstakeAmount, setUnstakeAmount] = useState("");
  const { address, isConnected } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider("eip155");

  // Cache busting version
  const appVersion = process.env.REACT_APP_VERSION || "1.0.2";
  const buildTime = process.env.REACT_APP_BUILD_TIME || Date.now();

  // Function to format address for display
  const formatAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Function to format balance for display
  const formatBalance = (balance) => {
    const num = parseFloat(balance);
    if (num === 0) return "0";
    if (num < 0.0001) return "< 0.0001";
    if (num < 1) return num.toFixed(6);
    if (num < 1000) return num.toFixed(4);
    if (num < 1000000) return (num / 1000).toFixed(2) + "K";
    return (num / 1000000).toFixed(2) + "M";
  };

  // Function to fetch token balance
  const fetchTokenBalance = async () => {
    if (!address || !isConnected) {
      setTokenBalance("0");
      return;
    }

    try {
      setIsLoadingBalance(true);

      // Connect to BSC mainnet
      const provider = new ethers.JsonRpcProvider(
        "https://bsc-dataseed1.binance.org/"
      );

      // Create contract instance
      const tokenContract = new ethers.Contract(
        appChain56.TOKEN_ADDRESS,
        erc20Abi,
        provider
      );

      // Get balance
      const balance = await tokenContract.balanceOf(address);

      // Get decimals
      const decimals = await tokenContract.decimals();

      // Format balance
      const formattedBalance = ethers.formatUnits(balance, decimals);
      setTokenBalance(formattedBalance);
    } catch (error) {
      console.error("Error fetching token balance:", error);
      setTokenBalance("0");
    } finally {
      setIsLoadingBalance(false);
    }
  };

  // Function to fetch migration data
  const fetchMigrationData = async () => {
    if (!address || !isConnected) {
      setBalanceTokenV1("0");
      setBalanceStakingCompoundV1("0");
      setBalanceStakingManualV1("0");
      setIsMigrated(false);
      return;
    }

    try {
      setIsLoadingMigration(true);
      const provider = new ethers.JsonRpcProvider(
        "https://bsc-dataseed1.binance.org/"
      );
      const migrationContract = new ethers.Contract(
        appChain56.MIGRATION_ADDRESS,
        migrationAbi,
        provider
      );

      // Get migration balances
      const [tokenV1, stakingCompoundV1, stakingManualV1, migrated] =
        await Promise.all([
          migrationContract.balanceTokenV1(address),
          migrationContract.balanceStakingCompoundV1(address),
          migrationContract.balanceStakingManualV1(address),
          migrationContract.isMigrated(address),
        ]);

      // Format values
      setBalanceTokenV1(ethers.formatUnits(tokenV1, 18));
      setBalanceStakingCompoundV1(ethers.formatUnits(stakingCompoundV1, 18));
      setBalanceStakingManualV1(ethers.formatUnits(stakingManualV1, 18));
      setIsMigrated(migrated);
    } catch (error) {
      console.error("Error fetching migration data:", error);
      setBalanceTokenV1("0");
      setBalanceStakingCompoundV1("0");
      setBalanceStakingManualV1("0");
      setIsMigrated(false);
    } finally {
      setIsLoadingMigration(false);
    }
  };

  // Function to fetch APY data from API
  const fetchAPYData = async () => {
    try {
      setIsLoadingAPY(true);
      const stakingInfo = await stakingApi.getStakingInfo(appChain56.STAKING_ADDRESS);

      // Update APY from API
      setApyRate(stakingInfo.apy || "0");
      setAprRate(stakingInfo.apr || "0");
    } catch (error) {
      console.error("Error fetching APY data:", error);
      // Keep existing values on error
    } finally {
      setIsLoadingAPY(false);
    }
  };

  // Function to fetch staking data
  const fetchStakingData = async () => {
    if (!address || !isConnected) {
      setStakedBalance("0");
      setPendingRewards("0");
      return;
    }

    try {
      const provider = new ethers.JsonRpcProvider(
        "https://bsc-dataseed1.binance.org/"
      );
      const stakingContract = new ethers.Contract(
        appChain56.STAKING_ADDRESS,
        stakingAbi,
        provider
      );

      // Get staked balance (shares)
      const shares = await stakingContract.shares(address);
      const stakedAmount = shares[0]; // amount field from shares struct

      // Get pending rewards
      const dividends = await stakingContract.dividendOf(address);

      // Format values
      setStakedBalance(ethers.formatUnits(stakedAmount, 18));
      setPendingRewards(ethers.formatUnits(dividends, 18));
    } catch (error) {
      console.error("Error fetching staking data:", error);
      setStakedBalance("0");
      setPendingRewards("0");
    }
  };

  // Function to fetch allowance
  const fetchAllowance = async () => {
    if (!address || !isConnected) {
      setAllowanceAmount("0");
      return;
    }

    try {
      const provider = new ethers.JsonRpcProvider(
        "https://bsc-dataseed1.binance.org/"
      );
      const tokenContract = new ethers.Contract(
        appChain56.TOKEN_ADDRESS,
        erc20Abi,
        provider
      );

      const allowance = await tokenContract.allowance(
        address,
        appChain56.STAKING_ADDRESS
      );
      setAllowanceAmount(ethers.formatUnits(allowance, 18));
    } catch (error) {
      console.error("Error fetching allowance:", error);
      setAllowanceAmount("0");
    }
  };

  // Fetch APY data on mount (independent of wallet connection)
  useEffect(() => {
    fetchAPYData();
  }, []);

  // Fetch balance and staking data on mount and when address changes
  useEffect(() => {
    fetchTokenBalance();
    fetchStakingData();
    fetchMigrationData();
    fetchAllowance();
  }, [address, isConnected]);

  const handleApprove = async () => {
    if (!address || !isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }

    if (!stakingAmount || parseFloat(stakingAmount) <= 0) {
      toast.error("Please enter a valid amount to approve");
      return;
    }

    // Determine which provider to use
    let activeProvider = walletProvider;

    if (!walletProvider || !walletProvider.request) {
      if (window.ethereum) {
        activeProvider = window.ethereum;
      } else {
        toast.error(
          "No wallet provider found. Please install MetaMask or connect your wallet."
        );
        return;
      }
    }

    try {
      setIsApproving(true);

      const provider = new ethers.BrowserProvider(activeProvider);
      const signer = await provider.getSigner();
      const tokenContract = new ethers.Contract(
        appChain56.TOKEN_ADDRESS,
        erc20Abi,
        signer
      );

      const amountWei = ethers.parseUnits(stakingAmount, 18);

      toast.info("Approving tokens...");
      const approveTx = await tokenContract.approve(
        appChain56.STAKING_ADDRESS,
        amountWei
      );
      await approveTx.wait();

      toast.success("Tokens approved successfully!");

      // Refresh allowance
      await fetchAllowance();
    } catch (error) {
      console.error("Approval error:", error);

      if (error.code === 4001) {
        toast.error("Transaction was rejected by user");
      } else if (error.reason) {
        toast.error("Approval failed: " + error.reason);
      } else if (error.message) {
        toast.error("Approval failed: " + error.message);
      } else {
        toast.error("Approval failed. Please try again.");
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleStaking = async () => {
    if (!address || !isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }

    if (!stakingAmount || parseFloat(stakingAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    // Debug wallet provider
    console.log("WalletProvider:", walletProvider);
    console.log("IsConnected:", isConnected);
    console.log("Address:", address);
    console.log("Window.ethereum:", window.ethereum);

    // Determine which provider to use
    let activeProvider = walletProvider;

    // Fallback to window.ethereum if walletProvider is not available or invalid
    if (!walletProvider || !walletProvider.request) {
      if (window.ethereum) {
        activeProvider = window.ethereum;
        console.log("Using window.ethereum as fallback");
      } else {
        toast.error(
          "No wallet provider found. Please install MetaMask or connect your wallet."
        );
        return;
      }
    }

    try {
      setIsStaking(true);

      // Use the active provider
      const provider = new ethers.BrowserProvider(activeProvider);
      const signer = await provider.getSigner();

      // Verify we're on the correct network
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== 56) {
        toast.error("Please switch to BSC Mainnet (Chain ID: 56)");
        return;
      }

      // Create contract instances
      const tokenContract = new ethers.Contract(
        appChain56.TOKEN_ADDRESS,
        erc20Abi,
        signer
      );
      const stakingContract = new ethers.Contract(
        appChain56.STAKING_ADDRESS,
        stakingAbi,
        signer
      );

      const amountWei = ethers.parseUnits(stakingAmount, 18);

      // Check user balance
      const balance = await tokenContract.balanceOf(address);
      console.log("User balance:", ethers.formatUnits(balance, 18));
      console.log("Amount to stake:", ethers.formatUnits(amountWei, 18));

      if (balance < amountWei) {
        toast.error("Insufficient token balance");
        return;
      }

      // Check allowance
      const allowance = await tokenContract.allowance(
        address,
        appChain56.STAKING_ADDRESS
      );
      console.log("Current allowance:", ethers.formatUnits(allowance, 18));
      console.log("Token address:", appChain56.TOKEN_ADDRESS);
      console.log("Staking address:", appChain56.STAKING_ADDRESS);

      if (allowance < amountWei) {
        toast.info("Approving tokens...");
        console.log("Approving tokens for staking...");

        const approveTx = await tokenContract.approve(
          appChain56.STAKING_ADDRESS,
          amountWei
        );
        console.log("Approve transaction sent:", approveTx.hash);

        const approveReceipt = await approveTx.wait();
        console.log("Approve transaction confirmed:", approveReceipt);

        toast.success("Tokens approved!");

        // Verify approval was successful
        const newAllowance = await tokenContract.allowance(
          address,
          appChain56.STAKING_ADDRESS
        );
        console.log(
          "New allowance after approval:",
          ethers.formatUnits(newAllowance, 18)
        );
      } else {
        console.log("Sufficient allowance already exists");
      }

      // Stake tokens
      toast.info("Staking tokens...");
      console.log("Starting stake transaction...");

      const stakeTx = await stakingContract.stake(address, amountWei);
      console.log("Stake transaction sent:", stakeTx.hash);

      const stakeReceipt = await stakeTx.wait();
      console.log("Stake transaction confirmed:", stakeReceipt);

      toast.success("Tokens staked successfully!");
      setStakingAmount("");

      // Refresh data
      await fetchTokenBalance();
      await fetchStakingData();
    } catch (error) {
      console.error("Staking error:", error);

      // Better error handling
      if (error.code === 4001) {
        toast.error("Transaction was rejected by user");
      } else if (error.code === -32603) {
        toast.error("Internal JSON-RPC error. Please try again.");
      } else if (error.reason) {
        toast.error("Staking failed: " + error.reason);
      } else if (error.message) {
        toast.error("Staking failed: " + error.message);
      } else {
        toast.error("Staking failed. Please try again.");
      }
    } finally {
      setIsStaking(false);
    }
  };

  const handleClaimReward = async () => {
    if (!address || !isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }

    // Determine which provider to use
    let activeProvider = walletProvider;

    // Fallback to window.ethereum if walletProvider is not available or invalid
    if (!walletProvider || !walletProvider.request) {
      if (window.ethereum) {
        activeProvider = window.ethereum;
      } else {
        toast.error(
          "No wallet provider found. Please install MetaMask or connect your wallet."
        );
        return;
      }
    }

    try {
      setIsClaiming(true);

      const provider = new ethers.BrowserProvider(activeProvider);
      const signer = await provider.getSigner();
      const stakingContract = new ethers.Contract(
        appChain56.STAKING_ADDRESS,
        stakingAbi,
        signer
      );

      toast.info("Claiming rewards...");
      const claimTx = await stakingContract.claimReward(address);
      await claimTx.wait();

      toast.success("Rewards claimed successfully!");

      await fetchStakingData();
    } catch (error) {
      console.error("Claim error:", error);

      if (error.code === 4001) {
        toast.error("Transaction was rejected by user");
      } else if (error.reason) {
        toast.error("Claim failed: " + error.reason);
      } else if (error.message) {
        toast.error("Claim failed: " + error.message);
      } else {
        toast.error("Claim failed. Please try again.");
      }
    } finally {
      setIsClaiming(false);
    }
  };

  const handleUnstakeAll = async () => {
    if (!address || !isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }

    if (parseFloat(stakedBalance) <= 0) {
      toast.error("No tokens to unstake");
      return;
    }

    // Determine which provider to use
    let activeProvider = walletProvider;

    // Fallback to window.ethereum if walletProvider is not available or invalid
    if (!walletProvider || !walletProvider.request) {
      if (window.ethereum) {
        activeProvider = window.ethereum;
      } else {
        toast.error(
          "No wallet provider found. Please install MetaMask or connect your wallet."
        );
        return;
      }
    }

    try {
      setIsUnstaking(true);

      const provider = new ethers.BrowserProvider(activeProvider);
      const signer = await provider.getSigner();

      // Verify we're on the correct network
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== 56) {
        toast.error("Please switch to BSC Mainnet (Chain ID: 56)");
        return;
      }

      const stakingContract = new ethers.Contract(
        appChain56.STAKING_ADDRESS,
        stakingAbi,
        signer
      );

      toast.info("Unstaking all tokens...");
      const unstakeTx = await stakingContract.unstakeAll(address);
      await unstakeTx.wait();

      toast.success("All tokens unstaked successfully!");

      await fetchStakingData();
      await fetchTokenBalance();
    } catch (error) {
      console.error("Unstake error:", error);

      // Better error handling
      if (error.code === 4001) {
        toast.error("Transaction was rejected by user");
      } else if (error.reason) {
        toast.error("Unstake failed: " + error.reason);
      } else if (error.message) {
        toast.error("Unstake failed: " + error.message);
      } else {
        toast.error("Unstake failed. Please try again.");
      }
    } finally {
      setIsUnstaking(false);
    }
  };

  const handleUnstake = async () => {
    if (!address || !isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }

    if (!unstakeAmount || parseFloat(unstakeAmount) <= 0) {
      toast.error("Please enter a valid amount to unstake");
      return;
    }

    if (parseFloat(unstakeAmount) > parseFloat(stakedBalance)) {
      toast.error("Insufficient staked balance");
      return;
    }

    // Determine which provider to use
    let activeProvider = walletProvider;

    // Fallback to window.ethereum if walletProvider is not available or invalid
    if (!walletProvider || !walletProvider.request) {
      if (window.ethereum) {
        activeProvider = window.ethereum;
      } else {
        toast.error(
          "No wallet provider found. Please install MetaMask or connect your wallet."
        );
        return;
      }
    }

    try {
      setIsUnstaking(true);

      const provider = new ethers.BrowserProvider(activeProvider);
      const signer = await provider.getSigner();

      // Verify we're on the correct network
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== 56) {
        toast.error("Please switch to BSC Mainnet (Chain ID: 56)");
        return;
      }

      const stakingContract = new ethers.Contract(
        appChain56.STAKING_ADDRESS,
        stakingAbi,
        signer
      );

      const amountWei = ethers.parseUnits(unstakeAmount, 18);

      toast.info("Unstaking tokens...");
      const unstakeTx = await stakingContract.unstake(address, amountWei);
      await unstakeTx.wait();

      toast.success("Tokens unstaked successfully!");
      setUnstakeAmount("");

      await fetchStakingData();
      await fetchTokenBalance();
    } catch (error) {
      console.error("Unstake error:", error);

      // Better error handling
      if (error.code === 4001) {
        toast.error("Transaction was rejected by user");
      } else if (error.reason) {
        toast.error("Unstake failed: " + error.reason);
      } else if (error.message) {
        toast.error("Unstake failed: " + error.message);
      } else {
        toast.error("Unstake failed. Please try again.");
      }
    } finally {
      setIsUnstaking(false);
    }
  };

  const handleMigration = async () => {
    if (!address || !isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }

    if (isMigrated) {
      toast.error("You have already migrated your tokens");
      return;
    }

    // Check if user has any tokens to migrate
    const totalToMigrate =
      parseFloat(balanceTokenV1) +
      parseFloat(balanceStakingCompoundV1) +
      parseFloat(balanceStakingManualV1);
    if (totalToMigrate <= 0) {
      toast.error("No tokens available for migration");
      return;
    }

    try {
      setIsMigrating(true);

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      // Get V1 token address from migration contract
      const migrationContract = new ethers.Contract(
        appChain56.MIGRATION_ADDRESS,
        migrationAbi,
        signer
      );
      const tokenV1Address = await migrationContract.tokenAddressV1();

      // Create V1 token contract instance
      const tokenV1Contract = new ethers.Contract(
        tokenV1Address,
        erc20Abi,
        signer
      );

      // Check if we need to approve V1 tokens
      if (parseFloat(balanceTokenV1) > 0) {
        const balanceV1Wei = ethers.parseUnits(balanceTokenV1, 18);
        const allowance = await tokenV1Contract.allowance(
          address,
          appChain56.MIGRATION_ADDRESS
        );

        if (allowance < balanceV1Wei) {
          toast.info("Approving V1 tokens for migration...");
          const approveTx = await tokenV1Contract.approve(
            appChain56.MIGRATION_ADDRESS,
            balanceV1Wei
          );
          await approveTx.wait();
          toast.success("V1 tokens approved!");
        }
      }

      // Execute migration
      toast.info("Starting migration process...");
      const migrateTx = await migrationContract.migrate();
      await migrateTx.wait();

      toast.success("Migration completed successfully!");

      // Refresh data
      await fetchMigrationData();
      await fetchTokenBalance();
    } catch (error) {
      console.error("Migration error:", error);
      toast.error("Migration failed: " + (error.reason || error.message));
    } finally {
      setIsMigrating(false);
    }
  };

  const copyToClipboard = (address) => {
    navigator.clipboard.writeText(address);
    setShowCopyNotification(true);
    setTimeout(() => {
      setShowCopyNotification(false);
    }, 2000);
  };

  return (
    <>
      <Header />

      {/* Elegant Banner Section */}
      <section className="relative px-4 py-8 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 md:px-12 lg:px-28 dark:from-grayc-950 dark:to-grayc-900 md:py-16 lg:py-20 font-body">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_800px_600px_at_50%_-100px,rgba(254,219,55,0.1),transparent)] dark:bg-[radial-gradient(ellipse_800px_600px_at_50%_-100px,rgba(254,219,55,0.05),transparent)]"></div>

        <div className="relative z-10 text-center space-y-6">
          {/* <div
            className="inline-flex items-center px-6 py-3 rounded-full border backdrop-blur-sm"
            style={{
              borderColor: "#10448A",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <RiCoinsLine className="mr-2" style={{ color: "#10448A" }} />
            <span className="font-semibold flex items-baseline">
              <span
                style={{
                  color: "#10448A",
                  fontWeight: "900",
                  fontSize: "1.5em",
                  letterSpacing: "0.5px",
                }}
              >
                BlockSAFU
              </span>
              <span
                className="ml-2"
                style={{
                  fontWeight: "900",
                  color: "#10448A",
                  fontSize: "1.5em",
                }}
              >
                BSC
              </span>
            </span>
          </div> */}

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight">
            <span
              className="text-transparent bg-clip-text"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              BlockSAFU BSC
            </span>
            <br />
            <span className="text-grayc-800 dark:text-white">
              <span className="inline-block mt-4">More than Staking</span>
              <br />
              It's Building the Future, Together.
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-grayc-600 dark:text-grayc-300 max-w-3xl mx-auto leading-relaxed">
            Welcome to the{" "}
            <span
              className="text-transparent bg-clip-text font-bold"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              BlockSAFU BSC
            </span>{" "}
            relaunch! Migrate your old Blocksafu tokens to the new{" "}
            <span
              className="text-transparent bg-clip-text font-bold"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              BSAFU
            </span>{" "}
            ecosystem and claim your staking rewards from our enhanced platform.
          </p>

          {/* <div className="flex flex-wrap justify-center gap-4 pt-4">
            <div className="px-6 py-2 bg-white/60 dark:bg-grayc-800/60 backdrop-blur-sm rounded-lg border border-grayc-200 dark:border-grayc-700">
              <span className="text-sm text-grayc-600 dark:text-grayc-400">
                Total Supply:
              </span>
              <span className="ml-2 font-bold text-yellowc-600 dark:text-yellowc-400">
                10,000,000 BSAFU
              </span>
        </div>
            <div className="px-6 py-2 bg-white/60 dark:bg-grayc-800/60 backdrop-blur-sm rounded-lg border border-grayc-200 dark:border-grayc-700">
              <span className="text-sm text-grayc-600 dark:text-grayc-400">
                Available for Claim:
              </span>
              <span className="ml-2 font-bold text-green-600 dark:text-green-400">
                Ready
              </span>
            </div>
          </div> */}
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellowc-400/20 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-yellowc-400/10 rounded-full blur-2xl"></div>
      </section>

      {/* Tabs Section */}
      <section className="relative px-4 py-12 bg-white dark:bg-grayc-900 md:px-12 lg:px-28 font-body">
        <div className="max-w-6xl mx-auto">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-grayc-100 dark:bg-grayc-800 rounded-xl p-1">
              <button
                onClick={() => setActiveTab("staking")}
                className={`flex items-center px-8 py-4 rounded-lg font-semibold transition-all duration-300 ${activeTab === "staking"
                    ? "bg-white dark:bg-grayc-700 shadow-md"
                    : "text-grayc-600 dark:text-grayc-400 hover:text-grayc-800 dark:hover:text-grayc-200"
                  }`}
                style={activeTab === "staking" ? { color: "#10448A" } : {}}
              >
                <RiStackLine className="mr-2 text-xl" />
                My Staking
              </button>
              <button
                onClick={() => setActiveTab("migrate")}
                className={`flex items-center px-8 py-4 rounded-lg font-semibold transition-all duration-300 ${activeTab === "migrate"
                    ? "bg-white dark:bg-grayc-700 shadow-md"
                    : "text-grayc-600 dark:text-grayc-400 hover:text-grayc-800 dark:hover:text-grayc-200"
                  }`}
                style={activeTab === "migrate" ? { color: "#10448A" } : {}}
              >
                <RiWalletLine className="mr-2 text-xl" />
                Migrate
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white dark:bg-grayc-800 rounded-2xl shadow-xl p-8 md:p-12">
            {activeTab === "token" && (
              <div className="space-y-8">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-grayc-800 dark:text-white mb-4">
                    My Token Balance
                  </h2>
                  <p className="text-grayc-600 dark:text-grayc-300">
                    View and manage your Blocksafu BSC token
                  </p>
                </div>

                {/* Action Card */}
                <div
                  className="rounded-xl p-8 text-center"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(16, 68, 138, 0.1), rgba(30, 90, 138, 0.15), rgba(37, 99, 235, 0.1))",
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                    style={{
                      background:
                        "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                    }}
                  >
                    <RiCoinsLine className="text-2xl text-white" />
                  </div>

                  {/* Token Balance Display */}
                  <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(16, 68, 138, 0.1)",
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="text-left text-grayc-700 dark:text-grayc-200 font-medium text-sm mb-1">
                            Balance BSAFU
                          </span>
                          <div className="flex items-center gap-1">
                            {" "}
                            <span className="text-xs text-grayc-500 dark:text-grayc-400 font-mono">
                              {" "}
                              0x1234...5678{" "}
                            </span>{" "}
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  "0x1234567890abcdef1234567890abcdef12345678"
                                )
                              }
                              className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors"
                            >
                              {" "}
                              <RiFileCopy2Line className="w-3 h-3 text-grayc-500 dark:text-grayc-400" />{" "}
                            </button>{" "}
                          </div>
                        </div>
                        <span className="font-bold text-grayc-800 dark:text-white">
                          0
                        </span>
                      </div>
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(30, 90, 138, 0.1)",
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="text-left text-grayc-700 dark:text-grayc-200 font-medium text-sm mb-1">
                            Balance BSAFU SP
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-grayc-500 dark:text-grayc-400 font-mono">
                              0xabcd...abcd
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  "0xabcdefabcdefabcdefabcdefabcdefabcdefabcd"
                                )
                              }
                              className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors"
                            >
                              <RiFileCopy2Line className="w-3 h-3 text-grayc-500 dark:text-grayc-400" />
                            </button>
                          </div>
                        </div>
                        <span className="font-bold text-grayc-800 dark:text-white">
                          0
                        </span>
                      </div>
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(37, 99, 235, 0.1)",
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="text-left text-grayc-700 dark:text-grayc-200 font-medium text-sm mb-1">
                            Balance BSAFU SP AUTO
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-grayc-500 dark:text-grayc-400 font-mono">
                              0x9999...3333
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  "0x9999999999111111111122222222223333333333"
                                )
                              }
                              className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors"
                            >
                              <RiFileCopy2Line className="w-3 h-3 text-grayc-500 dark:text-grayc-400" />
                            </button>
                          </div>
                        </div>
                        <span className="font-bold text-grayc-800 dark:text-white">
                          0
                        </span>
                      </div>
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(16, 68, 138, 0.15)",
                        }}
                      >
                        <div className="flex flex-col">
                          <span
                            className="text-left font-bold text-sm mb-1"
                            style={{
                              backgroundImage:
                                "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            Balance BSAFU BSC
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-grayc-500 dark:text-grayc-400 font-mono">
                              {formatAddress(appChain56.TOKEN_ADDRESS)}
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(appChain56.TOKEN_ADDRESS)
                              }
                              className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors"
                            >
                              <RiFileCopy2Line className="w-3 h-3 text-grayc-500 dark:text-grayc-400" />
                            </button>
                          </div>
                        </div>
                        <span className="font-bold text-grayc-800 dark:text-white">
                          0
                        </span>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-grayc-800 dark:text-white mb-2">
                    {isConnected
                      ? "Migration Available"
                      : "Connect Your Wallet"}
                  </h3>
                  <p className="text-grayc-600 dark:text-grayc-300 mb-6">
                    {isConnected
                      ? "Migrate your tokens to the new Blocksafu BSC platform"
                      : "Connect your wallet to access migration and token management features"}
                  </p>
                  <div className="flex justify-center">
                    {isConnected ? (
                      <button
                        className="px-8 py-4 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                        style={{
                          background:
                            "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background =
                            "linear-gradient(to right, #0D3B7A, #1A4F7A, #1E40AF)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background =
                            "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)";
                        }}
                      >
                        Migrate
                      </button>
                    ) : (
                      <w3m-button />
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "migrate" && (
              <div className="space-y-8">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-grayc-800 dark:text-white mb-4">
                    Token Migration
                  </h2>
                  <p className="text-grayc-600 dark:text-grayc-300">
                    Migrate your old Blocksafu tokens to the new BSAFU ecosystem
                  </p>
                </div>

                {/* Action Card */}
                <div
                  className="rounded-xl p-8 text-center"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(16, 68, 138, 0.1), rgba(30, 90, 138, 0.15), rgba(37, 99, 235, 0.1))",
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                    style={{
                      background:
                        "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                    }}
                  >
                    <RiWalletLine className="text-2xl text-white" />
                  </div>

                  {/* Migration Balance Display */}
                  <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(16, 68, 138, 0.1)",
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="text-left text-grayc-700 dark:text-grayc-200 font-medium text-sm mb-1">
                            Balance Token V1
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-grayc-500 dark:text-grayc-400 font-mono">
                              Available to migrate
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-grayc-800 dark:text-white">
                            {isLoadingMigration ? (
                              <div className="animate-pulse bg-grayc-300 dark:bg-grayc-600 h-4 w-16 rounded"></div>
                            ) : (
                              formatBalance(balanceTokenV1)
                            )}
                          </span>
                          <button
                            onClick={fetchMigrationData}
                            disabled={isLoadingMigration}
                            className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors disabled:opacity-50"
                            title="Refresh migration data"
                          >
                            <RiRefreshLine
                              className={`w-3 h-3 text-grayc-500 dark:text-grayc-400 ${isLoadingMigration ? "animate-spin" : ""
                                }`}
                            />
                          </button>
                        </div>
                      </div>
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(30, 90, 138, 0.1)",
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="text-left text-grayc-700 dark:text-grayc-200 font-medium text-sm mb-1">
                            Staking Compound V1
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-grayc-500 dark:text-grayc-400 font-mono">
                              Compound rewards
                            </span>
                          </div>
                        </div>
                        <span className="font-bold text-grayc-800 dark:text-white">
                          {isLoadingMigration ? (
                            <div className="animate-pulse bg-grayc-300 dark:bg-grayc-600 h-4 w-16 rounded"></div>
                          ) : (
                            formatBalance(balanceStakingCompoundV1)
                          )}
                        </span>
                      </div>
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(37, 99, 235, 0.1)",
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="text-left text-grayc-700 dark:text-grayc-200 font-medium text-sm mb-1">
                            Staking Manual V1
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-grayc-500 dark:text-grayc-400 font-mono">
                              Manual rewards
                            </span>
                          </div>
                        </div>
                        <span className="font-bold text-grayc-800 dark:text-white">
                          {isLoadingMigration ? (
                            <div className="animate-pulse bg-grayc-300 dark:bg-grayc-600 h-4 w-16 rounded"></div>
                          ) : (
                            formatBalance(balanceStakingManualV1)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-grayc-800 dark:text-white mb-2">
                    {!isConnected
                      ? "Connect Your Wallet"
                      : isMigrated
                        ? "Migration Completed"
                        : "Start Migration"}
                  </h3>
                  <p className="text-grayc-600 dark:text-grayc-300 mb-6">
                    {!isConnected
                      ? "Connect your wallet to check your migration eligibility"
                      : isMigrated
                        ? "You have successfully migrated all your tokens"
                        : "Migrate all your eligible tokens and rewards to the new BSAFU ecosystem"}
                  </p>

                  <div className="flex justify-center">
                    {isConnected ? (
                      <button
                        onClick={handleMigration}
                        disabled={
                          isMigrating ||
                          isMigrated ||
                          parseFloat(balanceTokenV1) +
                          parseFloat(balanceStakingCompoundV1) +
                          parseFloat(balanceStakingManualV1) <=
                          0
                        }
                        className="px-8 py-4 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          background:
                            isMigrated ||
                              parseFloat(balanceTokenV1) +
                              parseFloat(balanceStakingCompoundV1) +
                              parseFloat(balanceStakingManualV1) <=
                              0
                              ? "#9CA3AF"
                              : "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                        }}
                        onMouseEnter={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.background =
                              "linear-gradient(to right, #0D3B7A, #1A4F7A, #1E40AF)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.background =
                              "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)";
                          }
                        }}
                      >
                        {isMigrating
                          ? "Migrating..."
                          : isMigrated
                            ? "Already Migrated"
                            : "Migrate All Tokens"}
                      </button>
                    ) : (
                      <w3m-button />
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "staking" && (
              <div className="space-y-8">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-grayc-800 dark:text-white mb-4">
                    My Staking Portfolio
                  </h2>
                  <p className="text-grayc-600 dark:text-grayc-300">
                    Monitor your active stakes and rewards
                  </p>
                </div>

                {/* Action Card */}
                <div
                  className="rounded-xl p-8 text-center"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(16, 68, 138, 0.1), rgba(30, 90, 138, 0.15), rgba(37, 99, 235, 0.1))",
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                    style={{
                      background:
                        "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                    }}
                  >
                    <RiStackLine className="text-2xl text-white" />
                  </div>

                  {/* APY Display */}
                  <div className="mb-4">
                    <div className="text-sm font-medium text-grayc-600 dark:text-grayc-300 mb-1">
                      APY
                    </div>
                    <div className="text-3xl font-bold text-yellowc-600 dark:text-yellowc-400 mb-4 flex items-center justify-center gap-2">
                      {isLoadingAPY ? (
                        <div className="animate-pulse bg-yellowc-300 dark:bg-yellowc-600 h-8 w-24 rounded"></div>
                      ) : (
                        <>
                          {parseFloat(apyRate).toFixed(2)}%
                          <button
                            onClick={fetchAPYData}
                            disabled={isLoadingAPY}
                            className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors disabled:opacity-50"
                            title="Refresh APY data"
                          >
                            <RiRefreshLine
                              className={`w-4 h-4 text-grayc-500 dark:text-grayc-400 ${isLoadingAPY ? 'animate-spin' : ''}`}
                            />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Balance Display */}
                  <div className="mb-6">
                    <div className="grid grid-cols-1 gap-3 max-w-md mx-auto">
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(16, 68, 138, 0.15)",
                        }}
                      >
                        <div className="flex flex-col">
                          <span
                            className="text-left font-bold text-sm mb-1"
                            style={{
                              backgroundImage:
                                "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            Balance BSAFU BSC
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-grayc-500 dark:text-grayc-400 font-mono">
                              {formatAddress(appChain56.TOKEN_ADDRESS)}
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(appChain56.TOKEN_ADDRESS)
                              }
                              className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors"
                            >
                              <RiFileCopy2Line className="w-3 h-3 text-grayc-500 dark:text-grayc-400" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-grayc-800 dark:text-white">
                            {isLoadingBalance ? (
                              <div className="animate-pulse bg-grayc-300 dark:bg-grayc-600 h-4 w-16 rounded"></div>
                            ) : (
                              formatBalance(tokenBalance)
                            )}
                          </span>
                          <button
                            onClick={fetchTokenBalance}
                            disabled={isLoadingBalance}
                            className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors disabled:opacity-50"
                            title="Refresh balance"
                          >
                            <RiRefreshLine
                              className={`w-3 h-3 text-grayc-500 dark:text-grayc-400 ${isLoadingBalance ? "animate-spin" : ""
                                }`}
                            />
                          </button>
                        </div>
                      </div>
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(30, 90, 138, 0.1)",
                        }}
                      >
                        <span className="text-left text-grayc-700 dark:text-grayc-200 font-medium text-sm">
                          Unclaim Rewards
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-grayc-800 dark:text-white">
                            {formatBalance(pendingRewards)}
                          </span>
                          <button
                            onClick={fetchStakingData}
                            className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors"
                            title="Refresh staking data"
                          >
                            <RiRefreshLine className="w-3 h-3 text-grayc-500 dark:text-grayc-400" />
                          </button>
                        </div>
                      </div>
                      <div
                        className="backdrop-blur-sm rounded-lg p-3 flex justify-between items-center"
                        style={{
                          background: "rgba(37, 99, 235, 0.1)",
                        }}
                      >
                        <span className="text-left text-grayc-700 dark:text-grayc-200 font-medium text-sm">
                          Staked Amount
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-grayc-800 dark:text-white">
                            {formatBalance(stakedBalance)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isConnected && (
                    <>
                      <h3 className="text-2xl font-bold text-grayc-800 dark:text-white mb-2">
                        Connect Your Wallet
                      </h3>
                      <p className="text-grayc-600 dark:text-grayc-300 mb-6">
                        Connect your wallet to access staking features and start
                        earning rewards
                      </p>
                      <div className="flex justify-center mb-4">
                        <w3m-button />
                      </div>
                    </>
                  )}

                  {/* Staking Forms - Only show when wallet is connected */}
                  {isConnected && (
                    <div className="space-y-6 border-t border-grayc-400/30 pt-6">
                      {/* Staking Form */}
                      <div className="bg-grayc-50 dark:bg-grayc-700 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-grayc-800 dark:text-white mb-4 flex items-center">
                          <RiStackLine
                            className="mr-2 text-xl"
                            style={{ color: "#10448A" }}
                          />
                          Stake Tokens
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-grayc-700 dark:text-grayc-300 mb-2">
                              Amount to Stake
                            </label>
                            <div className="space-y-3">
                              <input
                                type="number"
                                value={stakingAmount}
                                onChange={(e) =>
                                  setStakingAmount(e.target.value)
                                }
                                placeholder="Enter amount"
                                className="w-full px-4 py-3 border border-grayc-300 dark:border-grayc-600 rounded-lg bg-white dark:bg-grayc-800 text-grayc-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />

                              {/* Check if approval is needed */}
                              {stakingAmount &&
                                parseFloat(stakingAmount) > 0 &&
                                parseFloat(allowanceAmount) <
                                parseFloat(stakingAmount) && (
                                  <button
                                    onClick={handleApprove}
                                    disabled={isApproving}
                                    className="w-full px-4 py-3 border-2 font-semibold rounded-lg hover:shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{
                                      borderColor: "#FFA500",
                                      color: "#FFA500",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!e.target.disabled) {
                                        e.target.style.backgroundColor =
                                          "#FFA500";
                                        e.target.style.color = "white";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!e.target.disabled) {
                                        e.target.style.backgroundColor =
                                          "transparent";
                                        e.target.style.color = "#FFA500";
                                      }
                                    }}
                                  >
                                    {isApproving
                                      ? "Approving..."
                                      : `Approve ${stakingAmount} BSAFU`}
                                  </button>
                                )}

                              <button
                                onClick={handleStaking}
                                disabled={
                                  !stakingAmount ||
                                  parseFloat(stakingAmount) <= 0 ||
                                  isStaking ||
                                  parseFloat(allowanceAmount) <
                                  parseFloat(stakingAmount || 0)
                                }
                                className="w-full px-4 py-3 text-white font-semibold rounded-lg hover:shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: "#10448A" }}
                                onMouseEnter={(e) =>
                                  !e.target.disabled &&
                                  (e.target.style.backgroundColor = "#0D3B7A")
                                }
                                onMouseLeave={(e) =>
                                  !e.target.disabled &&
                                  (e.target.style.backgroundColor = "#10448A")
                                }
                              >
                                {isStaking
                                  ? "Staking..."
                                  : parseFloat(allowanceAmount) <
                                    parseFloat(stakingAmount || 0)
                                    ? "Approve First"
                                    : "Stake"}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs text-grayc-600 dark:text-grayc-400">
                              <span>
                                Available: {formatBalance(tokenBalance)} BSAFU
                              </span>
                              <button
                                onClick={() => {
                                  // Kurangi 1500 token. Jika hasilnya <0 jadikan 0.
                                  const max = Math.max(
                                    0,
                                    parseFloat(tokenBalance) - 1500
                                  );
                                  setStakingAmount(
                                    max === 0 ? "0" : max.toString()
                                  );
                                }}
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                Use Max
                              </button>
                            </div>
                            <div className="flex justify-between text-xs text-grayc-600 dark:text-grayc-400">
                              <span>
                                Allowance: {formatBalance(allowanceAmount)}{" "}
                                BSAFU
                              </span>
                              <button
                                onClick={fetchAllowance}
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                Refresh
                              </button>
                            </div>
                            <div className="flex justify-between items-center text-xs text-grayc-600 dark:text-grayc-400">
                              <span>Staking Contract:</span>
                              <div className="flex items-center gap-1">
                                <span className="font-mono">
                                  {formatAddress(appChain56.STAKING_ADDRESS)}
                                </span>
                                <button
                                  onClick={() =>
                                    copyToClipboard(appChain56.STAKING_ADDRESS)
                                  }
                                  className="p-1 hover:bg-grayc-300 dark:hover:bg-grayc-600 rounded transition-colors"
                                  title="Copy staking contract address"
                                >
                                  <RiFileCopy2Line className="w-3 h-3 text-grayc-500 dark:text-grayc-400" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Claim Rewards */}
                        <div className="bg-grayc-50 dark:bg-grayc-700 rounded-xl p-6">
                          <h4 className="text-lg font-semibold text-grayc-800 dark:text-white mb-4 flex items-center">
                            <RiCoinsLine className="mr-2 text-xl text-green-600" />
                            Claim Rewards
                          </h4>
                          <div className="space-y-4">
                            <div className="text-center py-4">
                              <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                                {formatBalance(pendingRewards)} BSAFU
                              </div>
                              <div className="text-sm text-grayc-600 dark:text-grayc-400">
                                Available Rewards
                              </div>
                            </div>
                            <button
                              onClick={handleClaimReward}
                              disabled={
                                isClaiming || parseFloat(pendingRewards) <= 0
                              }
                              className="w-full px-4 py-3 border-2 border-green-500 text-green-600 hover:bg-green-500 hover:text-white font-semibold rounded-lg hover:shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isClaiming ? "Claiming..." : "Claim Rewards"}
                            </button>
                          </div>
                        </div>

                        {/* Unstake Section */}
                        <div className="bg-grayc-50 dark:bg-grayc-700 rounded-xl p-6">
                          <h4 className="text-lg font-semibold text-grayc-800 dark:text-white mb-4 flex items-center">
                            <RiWalletLine className="mr-2 text-xl text-red-600" />
                            Unstake Tokens
                          </h4>
                          <div className="space-y-4">
                            <div className="text-center py-4">
                              <div className="text-2xl font-bold text-grayc-800 dark:text-white mb-1">
                                {formatBalance(stakedBalance)} BSAFU
                              </div>
                              <div className="text-sm text-grayc-600 dark:text-grayc-400">
                                Total Staked Amount
                              </div>
                            </div>

                            {/* Unstake Amount Input */}
                            <div>
                              <label className="block text-sm font-medium text-grayc-700 dark:text-grayc-300 mb-2">
                                Amount to Unstake
                              </label>
                              <div className="space-y-3">
                                <input
                                  type="number"
                                  value={unstakeAmount}
                                  onChange={(e) =>
                                    setUnstakeAmount(e.target.value)
                                  }
                                  placeholder="Enter amount"
                                  className="w-full px-4 py-3 border border-grayc-300 dark:border-grayc-600 rounded-lg bg-white dark:bg-grayc-800 text-grayc-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />

                                <button
                                  onClick={handleUnstake}
                                  disabled={
                                    !unstakeAmount ||
                                    parseFloat(unstakeAmount) <= 0 ||
                                    parseFloat(unstakeAmount) >
                                    parseFloat(stakedBalance) ||
                                    isUnstaking
                                  }
                                  className="w-full px-4 py-3 border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white font-semibold rounded-lg hover:shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isUnstaking ? "Unstaking..." : "Unstake"}
                                </button>
                              </div>

                              <div className="flex justify-between text-xs text-grayc-600 dark:text-grayc-400 mt-2">
                                <span>
                                  Staked: {formatBalance(stakedBalance)} BSAFU
                                </span>
                                <button
                                  onClick={() =>
                                    setUnstakeAmount(stakedBalance)
                                  }
                                  className="text-red-600 dark:text-red-400 hover:underline"
                                >
                                  Use Max
                                </button>
                              </div>
                            </div>

                            {/* Unstake All Button */}
                            <div className="border-t border-grayc-300 dark:border-grayc-600 pt-3">
                              <button
                                onClick={handleUnstakeAll}
                                disabled={
                                  isUnstaking || parseFloat(stakedBalance) <= 0
                                }
                                className="w-full px-4 py-3 bg-red-600 text-white hover:bg-red-700 font-semibold rounded-lg hover:shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isUnstaking ? "Unstaking..." : "Unstake All"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />

      {/* Version Info */}
      <div className="fixed bottom-2 right-2 text-xs text-grayc-400 dark:text-grayc-600 bg-white/80 dark:bg-grayc-800/80 px-2 py-1 rounded backdrop-blur-sm">
        v{appVersion} | {new Date(buildTime).toLocaleTimeString()}
      </div>

      {/* Copy Notification */}
      {showCopyNotification && (
        <div
          className="fixed top-4 right-4 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2"
          style={{
            background: "linear-gradient(to right, #10448A, #1E5A8A, #2563EB)",
          }}
        >
          <RiFileCopy2Line className="w-4 h-4" />
          <span>Address Copied</span>
        </div>
      )}
    </>
  );
}
